/**
 * emphasisSpans — WHERE the **bold** / *italic* / _italic_ / __bold__ /
 * ~~strike~~ markers are in a line, so the live-preview editor can hide the
 * markers and style what is between them.
 *
 * (Not to be confused with lib/inlineEmphasis, which WRITES those markers for
 * the Format menu's Bold/Italic buttons. This module only reads.)
 *
 * Walter 2026-10-09: he wrote down a band's Instagram handle,
 * `@at_the_sundowners`, and it "renders funny" — `_the_` matched the italic
 * rule, so the underscores disappeared and "the" came out italic. Handles,
 * snake_case, file names and plenty of URLs are full of underscores and none
 * of them are emphasis.
 *
 * THE RULE: an underscore pair only emphasises at a word boundary — nothing
 * word-ish immediately before the opening marker or after the closing one.
 * `_hello_` and `(_hello_)` emphasise; `at_the_sundowners` and
 * `snake_case_name` do not. CommonMark draws the same line, which is why
 * other markdown editors leave such handles alone.
 *
 * Asterisks deliberately keep the old behaviour: `*` inside a word is rare in
 * prose, and changing it would be scope he did not ask for.
 *
 * Lives here, apart from MarkdownEditor, so it can be unit-tested at all —
 * that module pulls in CodeMirror, which Jest cannot load in this project.
 */

// Order matters: the two-character markers must be tried before the
// one-character ones, or `**bold**` would match as `*` + `*bold*`.
const EMPHASIS_RE = /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_|~~([^~]+)~~/g;

const isWordish = (ch) => ch !== undefined && /\w/.test(ch);

/**
 * findEmphasis(text) → [{ start, end, markerLen, cls }]
 *
 * `start`/`end` bound the WHOLE match, markers included; `markerLen` is the
 * length of each marker. Matches never overlap — the scan runs left to right
 * and never revisits consumed text.
 */
export function findEmphasis(text) {
  const s = String(text ?? '');
  const out = [];
  EMPHASIS_RE.lastIndex = 0;
  let m;
  while ((m = EMPHASIS_RE.exec(s))) {
    const start = m.index;
    const end = start + m[0].length;
    const isUnderscore = m[2] !== undefined || m[4] !== undefined;
    if (isUnderscore && (isWordish(s[start - 1]) || isWordish(s[end]))) {
      // Mid-word underscores: a handle, snake_case, a file name, a URL.
      continue;
    }
    let markerLen;
    let cls;
    if (m[1] !== undefined || m[2] !== undefined) { markerLen = 2; cls = 'md-bold'; }
    else if (m[5] !== undefined) { markerLen = 2; cls = 'md-strike'; }
    else { markerLen = 1; cls = 'md-ital'; }
    out.push({ start, end, markerLen, cls });
  }
  return out;
}

export default findEmphasis;
